import { z } from 'zod';
import { COMPANY_PHASE_ITEMS, LISTED_PHASE_ITEMS } from '../constants';
import { isCustomFieldValueEmpty } from '@/utils/customFieldValidation';
import type { SharedCustomField } from '@/types/serializers';

const enumFromItems = <T extends string>(items: ReadonlyArray<{ value: T }>) =>
  z.enum(items.map((i) => i.value) as unknown as readonly [T, ...T[]]);

// customFields を受け取り、必須カスタムフィールドの検証を含むスキーマを動的に生成する。
// 静的スキーマだと is_required の情報を持てず必須チェックができないため、面談情報更新モーダル
// （createCandidatesInterviewLogsSchema）と同じく superRefine で field_id 単位のエラーを付与する。
export const createCompaniesCreateSchema = (customFields: SharedCustomField[] = []) => {
  const requiredFields = customFields.filter((field) => field.is_required);

  return z.object({
    name: z.string().min(1, '＊企業名は必須項目です。入力してください。'),
    organization_user_id: z.number().int().optional(),
    company_information: z.object({
      description: z.string().optional(),
      president_name: z.string().optional(),
      official_url: z
        .string()
        .url('＊URLの形式が正しくありません。「https://」から始まるURLを入力してください。')
        // z.url() は http:// や ftp:// も許可するため、エラーメッセージ通り https:// 始まりに限定する
        .startsWith(
          'https://',
          '＊URLの形式が正しくありません。「https://」から始まるURLを入力してください。'
        )
        .optional()
        .or(z.literal('')),
      address: z
        .string({ error: '＊住所は必須項目です。入力してください。' })
        .min(1, '＊住所は必須項目です。入力してください。'),
      established_date: z
        .string()
        .transform((v) => v.replace(/\//g, '-'))
        .refine((v) => /^\d{4}-\d{1,2}-\d{1,2}$/.test(v), 'YYYY-MM-DD 形式で入力してください')
        .or(z.literal(''))
        .optional(),
      phase: enumFromItems(COMPANY_PHASE_ITEMS).optional(),
      listed_phase: enumFromItems(LISTED_PHASE_ITEMS).optional(),
      number_of_employees: z.union([z.number().int().nonnegative(), z.null()]).optional(),
      industry_id: z.number().int().optional(),
      corporate_number: z
        .string()
        .regex(/^\d{13}$/, '＊法人番号は13桁の半角数字で入力してください。')
        .optional()
        .or(z.literal('')),
    }),
    company_contract: z
      .object({
        contract_status: z.enum(['contracted', 'nothing', 'other']).optional(),
        fee_rate: z.number().optional(),
        minimum_fee: z.number().optional(),
        note: z.string().optional(),
      })
      .partial()
      .optional(),
    // カスタムフィールド値。キーは field_id 文字列、送信時に buildCustomDataPayload で配列へ変換する。
    // Controller がマウント時に undefined を登録するため、値は optional にして zod 検証を通す
    customData: z
      .record(z.string(), z.union([z.string(), z.number()]).optional())
      .superRefine((data, ctx) => {
        requiredFields.forEach((field) => {
          if (isCustomFieldValueEmpty(field, data?.[String(field.id)])) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [String(field.id)],
              message: `＊${field.name}は必須項目です。入力してください。`,
            });
          }
        });
      })
      .optional(),
    custom_data: z.array(z.object({ field_id: z.number().int(), content: z.string() })).optional(),
  });
};

export type CompaniesCreateInput = z.infer<ReturnType<typeof createCompaniesCreateSchema>>;
